/**
 * Query-token highlighting for result-card titles and descriptions.
 *
 * Rule from the design handoff: lowercase + split on whitespace + keep
 * tokens longer than 2 characters (so "of", "the", "ai" drop out). The
 * shorter-token filter is load-bearing — without it, highlighting one- or
 * two-letter function words produces a visually noisy card.
 *
 * Regex specials in tokens are escaped before building the match regex;
 * a query like `a+b (c)` otherwise blows up the regex compiler.
 */

import type { ReactNode } from "react";
import { Fragment } from "react";

const REGEX_SPECIALS = /[.*+?^${}()|[\]\\]/g;

export function tokenise(query: string | undefined | null): string[] {
  if (!query) return [];
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 2);
}

export function Mark({ children }: { children: ReactNode }) {
  return (
    <mark
      style={{
        background: "color-mix(in oklch, var(--warn) 40%, white)",
        color: "var(--foreground)",
        padding: "0 1px",
        borderRadius: 2,
      }}
    >
      {children}
    </mark>
  );
}

export function highlight(text: string | undefined | null, tokens: string[]): ReactNode {
  if (text === undefined || text === null) return null;
  if (!tokens || tokens.length === 0) return text;
  const pattern = tokens.map((t) => t.replace(REGEX_SPECIALS, "\\$&")).join("|");
  const re = new RegExp(`(${pattern})`, "gi");
  return text
    .split(re)
    .map((part, i) =>
      tokens.some((t) => part.toLowerCase() === t.toLowerCase()) ? (
        <Mark key={i}>{part}</Mark>
      ) : (
        <Fragment key={i}>{part}</Fragment>
      ),
    );
}
